import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheckboxGroup } from "@components/inputs/form/FormCheckboxGroup/FormCheckboxGroup";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useSubmitIncomeInformationMutation } from "@services/knowYourCustomer/mutations";
import { useIncomeInformationQuery } from "@services/knowYourCustomer/queries";
import {
  KycFormStep,
  type IIncomeInformation,
  type TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { isNullOrUndefined } from "@utils/common";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";
import { KYCModalPaging } from "../KYCModalPaging/KYCModalPaging";

interface IIncomeInformationFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const IncomeInformationForm: React.FC<IIncomeInformationFormProps> = ({
  onChangeStep,
}) => {
  const { t } = useTranslation();
  const { data: incomeInformation } = useIncomeInformationQuery();

  const { mutateAsync: submitIncomeInformation, isPending } =
    useSubmitIncomeInformationMutation();

  const { positionTypeOptions, activityTypeOptions, incomeSourceTypeOptions } =
    useMemo(() => {
      if (!incomeInformation) {
        return {
          positionTypeOptions: [],
          activityTypeOptions: [],
          incomeSourceTypeOptions: [],
        };
      }

      const positionTypeOptions = (incomeInformation.positionTypes ?? []).map(
        (positionType) => ({
          value: positionType.positionType,
          label: t(
            `KYC.modal.step3.form.fields.selectedPositionTypes.options.${positionType.positionType}`,
          ),
        }),
      );

      const activityTypeOptions =
        incomeInformation.activityTypes?.map((activityType) => ({
          value: activityType.activityType,
          label: t(
            `KYC.modal.step3.form.fields.activityType.options.${activityType.activityType}`,
          ),
        })) ?? [];

      const incomeSourceTypeOptions =
        incomeInformation.incomeSourceTypes?.map((incomeSource) => ({
          value: incomeSource.incomeSourceType,
          label: t(
            `KYC.modal.step3.form.fields.incomeSourceType.options.${incomeSource.incomeSourceType}`,
          ),
        })) ?? [];

      return {
        positionTypeOptions,
        activityTypeOptions,
        incomeSourceTypeOptions,
      };
    }, [incomeInformation, t]);

  const { handleSubmit, control, setError } = useForm<IIncomeInformation>({
    defaultValues: {
      activityType: undefined,
      activityTypeDescription: "",
      incomeSourceType: undefined,
      incomeSourceTypeDescription: "",
      selectedPositionTypes: [],
      positionTypeDescription: "",
    },
    values: incomeInformation,
  });

  const [activityTypeValue, incomeSourceTypeValue, selectedPositionTypeValues] =
    useWatch({
      name: ["activityType", "incomeSourceType", "selectedPositionTypes"],
      control,
    });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitIncomeInformationForm = async (
    data: IIncomeInformation,
    step?: TKnowYourCustomerStepAction,
  ) => {
    delete data.activityTypes;
    delete data.incomeSourceTypes;
    delete data.positionTypes;

    await submitIncomeInformation({ ...data, step })
      .then((response) => onChangeStep(response.currentForm))
      .catch(setApiErrors);
  };

  return (
    <div className="flex flex-col gap-6">
      <KYCModalPaging
        currentStep={KycFormStep.INCOME_INFORMATION_FORM}
        onPreviousButtonClick={handleSubmit((data) =>
          submitIncomeInformationForm(data, "previous"),
        )}
        onNextButtonClick={handleSubmit((data) =>
          submitIncomeInformationForm(data),
        )}
        isLoading={isPending}
      />
      <form
        onSubmit={handleSubmit((data) => submitIncomeInformationForm(data))}
        className="flex flex-col gap-3"
      >
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step3.form.fields.selectedPositionTypes.title")}
        </p>
        <FormCheckboxGroup
          name="selectedPositionTypes"
          options={positionTypeOptions}
          control={control}
        />
        {incomeInformation?.positionTypes?.some(
          (position) =>
            selectedPositionTypeValues.includes(position.positionType) &&
            position.shouldFillOut,
        ) && (
          <FormInput
            name="positionTypeDescription"
            control={control}
            placeholder={t(
              "KYC.modal.step3.form.fields.positionTypeDescription.placeholder",
            )}
          />
        )}
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step3.form.fields.incomeSourceType.title")}
        </p>
        <FormSelect
          name="incomeSourceType"
          options={incomeSourceTypeOptions}
          control={control}
          label={t("KYC.modal.step3.form.fields.incomeSourceType.label")}
        />
        {!isNullOrUndefined(incomeSourceTypeValue) &&
          incomeInformation?.incomeSourceTypes?.find(
            (incomeSource) =>
              incomeSource.incomeSourceType === incomeSourceTypeValue,
          )?.shouldFillOut && (
            <FormInput
              name="incomeSourceTypeDescription"
              control={control}
              placeholder={t(
                "KYC.modal.step3.form.fields.incomeSourceTypeDescription.placeholder",
              )}
            />
          )}
        {incomeInformation?.positionTypes?.some(
          (position) =>
            selectedPositionTypeValues.includes(position.positionType) &&
            position.mustHaveActivityType,
        ) && (
          <>
            <p className="text-gray-900 p-0 text-lg font-semibold">
              {t("KYC.modal.step3.form.fields.activityType.title")}
            </p>
            <FormSelect
              name="activityType"
              options={activityTypeOptions}
              control={control}
              label={t("KYC.modal.step3.form.fields.activityType.label")}
              shouldTruncateOptions={false}
            />
            {activityTypeValue &&
              incomeInformation?.activityTypes?.find(
                (activityType) =>
                  activityType.activityType === activityTypeValue,
              )?.shouldFillOut && (
                <FormInput
                  name="activityTypeDescription"
                  control={control}
                  placeholder={t(
                    "KYC.modal.step3.form.fields.activityTypeDescription.placeholder",
                  )}
                />
              )}
          </>
        )}
      </form>
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) => submitIncomeInformationForm(data))}
        onCancel={handleSubmit((data) =>
          submitIncomeInformationForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
